import {
  AccountingIcon,
  BankFeedsIcon,
  BankingIcon,
  BellIcon,
  CardIcon,
  ClusterIcon,
  CommerceIcon,
  EditIcon,
  LinkedIcon,
  PadlockIcon,
  PowerCordPlugIcon,
  SquareNewWindowIcon,
  StarEmptyIcon,
  UsersIcon,
} from "@codat/orchard-ui";
import { useGetIntegrationsCounts } from "api/portal";
import { useProducts } from "contexts/ProductsContext";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import { useUserState } from "portal-ui-remote/UserStateContext";
import { getProductDetails } from "utils/products";

import { paths } from "components/App/paths";
import { createNavPermissionMap } from "components/NavContainer/utils";

import { SettingsNavItemProps } from "./SettingsSection/SettingsNavItem";

const hasPermission = (item: SettingsNavItemProps & { permission: boolean }) =>
  item.permission;

interface ResolvedNavItems {
  authFlow: SettingsNavItemProps[];
  webhooks: SettingsNavItemProps[];
  integrations: SettingsNavItemProps[];
  organization: SettingsNavItemProps[];
  products: SettingsNavItemProps[];
}

export function useSettingsOverviewNavItems(): ResolvedNavItems {
  const { userState } = useUserState();
  const { featureStatus } = useFeatureStatus();
  const [{ data: integrationCounts }] = useGetIntegrationsCounts();
  const permissionsMap = createNavPermissionMap(
    userState ? userState.userPermissions : [],
    userState?.userRole
  );

  const { products } = useProducts();

  const authFlow = [
    {
      icon: StarEmptyIcon,
      name: "Branding",
      linkText: "Set branding",
      path: paths.settings.BRANDING,
      permission: permissionsMap.settings.branding.permission,
    },
    {
      icon: SquareNewWindowIcon,
      name: "Redirects",
      linkText: "Manage redirects",
      path: paths.settings.REDIRECTS,
      permission: permissionsMap.settings.redirects.permission,
    },
    {
      icon: LinkedIcon,
      name: "Link",
      linkText: "Customize Link",
      path: paths.settings.LINK_SETTINGS,
      permission: permissionsMap.settings.linkSettings.permission,
    },
  ];

  const webhooks = featureStatus?.["Rules.UseNewWebhooks"]
    ? [
        {
          icon: BellIcon,
          name: "Events",
          linkText: "Configure consumers",
          path: paths.monitor.EVENTS,
          permission: permissionsMap.monitor.events.permission,
        },
      ]
    : [
        {
          icon: EditIcon,
          name: "Rules",
          linkText: "Manage rules",
          path: paths.settings.RULES,
          permission: permissionsMap.settings.rules.permission,
        },
        {
          icon: PadlockIcon,
          name: "Security",
          linkText: "Manage security",
          path: paths.settings.WEBHOOK_SECURITY,
          permission: permissionsMap.settings.webhookSecurity.permission,
        },
      ];

  const integrations = [
    {
      icon: AccountingIcon,
      name: "Accounting",
      linkText: "Manage integrations",
      path: paths.settings.INTEGRATIONS_ACCOUNTING,
      permission: permissionsMap.settings.integrations.permission,
      integrationCounts: integrationCounts?.accounting,
    },
    {
      icon: BankingIcon,
      name: "Banking",
      linkText: "Manage integrations",
      path: paths.settings.INTEGRATIONS_BANKING,
      permission: permissionsMap.settings.integrations.permission,
    },
    {
      icon: BankFeedsIcon,
      name: "Bank feeds",
      linkText: "Manage integrations",
      path: paths.settings.INTEGRATIONS_BANK_FEEDS,
      permission: permissionsMap.settings.integrations.permission,
      integrationCounts: integrationCounts?.bankFeeds,
    },
    {
      icon: CommerceIcon,
      name: "Commerce",
      linkText: "Manage integrations",
      path: paths.settings.INTEGRATIONS_COMMERCE,
      permission: permissionsMap.settings.integrations.permission,
      integrationCounts: integrationCounts?.commerce,
    },
    {
      icon: PowerCordPlugIcon,
      name: "Other integrations",
      linkText: "Manage integrations",
      path: paths.settings.INTEGRATIONS_OTHER,
      permission: permissionsMap.settings.otherIntegrations.permission,
      integrationCounts: integrationCounts?.otherIntegrations,
    },
    {
      icon: ClusterIcon,
      name: "Data types",
      linkText: "Configure data types",
      path: paths.settings.DATA_TYPES,
      permission: permissionsMap.settings.dataTypes.permission,
      integrationCounts: integrationCounts?.dataTypes,
    },
  ];

  const productNavs = products
    .filter((p) => p.status === "Enabled")
    .map((p) => ({
      icon: getProductDetails(p).Icon,
      name: p.name,
      linkText: `Settings`,
      path: p.hasSettings ? paths.settings.PRODUCTS + "/" + p.key : undefined,
      permission: permissionsMap.settings.products.permission,
    }));

  const organization = [
    {
      icon: CardIcon,
      name: "Billing",
      linkText: "View billing",
      path: paths.settings.BILLING,
      permission: permissionsMap.settings.billing.permission,
    },
    {
      icon: UsersIcon,
      name: "Users",
      linkText: "Manage users",
      path: paths.settings.USERS,
      permission: permissionsMap.settings.users.permission,
    },
  ];

  return {
    authFlow: authFlow.filter(hasPermission),
    webhooks: webhooks.filter(hasPermission),
    integrations: integrations.filter(hasPermission),
    organization: organization.filter(hasPermission),
    products: productNavs.filter(hasPermission),
  };
}
